import React from 'react'
import { ButtonProps as AntButtonProps } from 'antd'
import {
  LoadingOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  DownloadOutlined,
  UploadOutlined,
  ReloadOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import { IconWrapper, StyledButton } from './styled'

export type ButtonType =
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'danger-outline'
  | 'warning'
  | 'success'
  | 'ghost'
  | 'text'
  | 'light'
  | 'dark'
  | 'link'
  | 'dashed'
  | 'transparent'
  | 'invisible'
  | 'icon'

export type ButtonSize = 'small' | 'middle' | 'large' | 'smallBtnIcon'
export type CustomIconPosition = 'left' | 'right'

export interface CustomButtonProps
  extends Omit<AntButtonProps, 'type' | 'size'> {
  type?: ButtonType
  size?: ButtonSize
  iconAlign?: CustomIconPosition
  customIcon?: React.ReactNode
  $fullWidth?: boolean
  $rounded?: boolean
}

export const Button: React.FC<CustomButtonProps> = ({
  type = 'primary',
  size = 'middle',
  iconAlign = 'left',
  customIcon,
  children,
  $fullWidth = false,
  $rounded = false,
  loading,
  disabled,
  ...props
}) => {
  const renderIcon = () => {
    if (loading) {
      return <LoadingOutlined />
    }
    return customIcon || null
  }

  const iconElement = renderIcon()
  const shouldShowIcon = iconElement && !loading

  return (
    <StyledButton
      type={type}
      size={size}
      $fullWidth={$fullWidth}
      $rounded={$rounded}
      loading={loading}
      disabled={disabled}
      {...props}
    >
      {shouldShowIcon && iconAlign === 'left' && (
        <IconWrapper align={iconAlign}>{iconElement}</IconWrapper>
      )}

      {children}

      {shouldShowIcon && iconAlign === 'right' && (
        <IconWrapper align={iconAlign}>{iconElement}</IconWrapper>
      )}
    </StyledButton>
  )
}

export const PrimaryButton: React.FC<Omit<CustomButtonProps, 'type'>> = (
  props
) => <Button type="primary" {...props} />

export const SecondaryButton: React.FC<Omit<CustomButtonProps, 'type'>> = (
  props
) => <Button type="secondary" {...props} />

export const DangerButton: React.FC<Omit<CustomButtonProps, 'type'>> = (
  props
) => <Button type="danger" {...props} />

export const DangerOutlineButton: React.FC<Omit<CustomButtonProps, 'type'>> = (
  props
) => <Button type="danger-outline" {...props} />

export const SuccessButton: React.FC<Omit<CustomButtonProps, 'type'>> = (
  props
) => <Button type="success" {...props} />

export const WarningButton: React.FC<Omit<CustomButtonProps, 'type'>> = (
  props
) => <Button type="warning" {...props} />

export const LightButton: React.FC<Omit<CustomButtonProps, 'type'>> = (
  props
) => <Button type="light" {...props} />

export const DarkButton: React.FC<Omit<CustomButtonProps, 'type'>> = (
  props
) => <Button type="dark" {...props} />

export const LinkButton: React.FC<Omit<CustomButtonProps, 'type'>> = (
  props
) => <Button type="link" {...props} />

export const InvisibleButton: React.FC<Omit<CustomButtonProps, 'type'>> = (
  props
) => <Button type="invisible" {...props} />

export const DashedButton: React.FC<Omit<CustomButtonProps, 'type'>> = (
  props
) => <Button type="dashed" {...props} />

export const IconButton: React.FC<Omit<CustomButtonProps, 'type'>> = (
  props
) => <Button type="icon" size="smallBtnIcon" {...props} />

export {
  PlusOutlined as AddIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
  SearchOutlined as SearchIcon,
  DownloadOutlined as DownloadIcon,
  UploadOutlined as UploadIcon,
  ReloadOutlined as RefreshIcon,
  CheckOutlined as CheckIcon,
  CloseOutlined as CloseIcon,
  EyeOutlined as EyeIcon,
}
